import React, { useState, useEffect } from "react";
import { Typography, TextField, Button } from "@mui/material";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../../ui/tabs";
import { connect } from "react-redux";
import PropTypes from "prop-types";

//Actions
import { setServingsEaten, setMacrosModalSection } from "../../../../../actions/walkthrough";

//UI
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../../ui/dropdown-menu";
import { Button as ShadcnButton } from "../../../../ui/button";

import { ChevronDown } from "lucide-react";
//TODO:
// Pass and update eaten servings in redux
// Max 2 decimal places allowed on eaten amount
//

const MacrosItem = ({ data, servings = 1 }) => {
  const amount = servings === 1 ? data.amount : data.amount * servings;

  return (
    <div>
      <div className="flex flex-row justify-between items-center">
        <Typography variant="subtitle1" className="font-normal leading-snug">
          {data.name}
        </Typography>
        <div className="flex-1 border-b border-separator mx-2"></div>
        <div className="flex flex-row">
          <Typography variant="body2">{amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}</Typography>
          {data.unit && (
            <Typography variant="body2" className="ml-1">
              {data.unit.name}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

const MacrosModal = ({
  handleClose,
  setServingsEaten,
  setMacrosModalSection,
  section,
  walkthrough: { recipe, modals },
}) => {
  //TODO: Error highlighting
  const [localServingsEaten, setLocalServingsEaten] = useState(section?.servingsEaten ? section.servingsEaten : 0);
  const [activeTab, setActiveTab] = useState("total");
  const [otherLoggableSections, setOtherLoggableSections] = useState([modals?.macrosModal?.sectionIndex]);

  const handleServingsChange = (e) => {
    const value = e.target.value;
    if (value === "" || isNaN(value)) {
      setLocalServingsEaten("");
      return;
    }

    const numValue = parseFloat(value);
    if (numValue <= 0) {
      setLocalServingsEaten("");
      return;
    }

    setLocalServingsEaten(numValue);
  };

  const handleSave = () => {
    setServingsEaten(modals?.macrosModal?.sectionIndex, localServingsEaten);
    handleClose();
  };

  const getOtherLoggableSections = () => {
    const loggableSections = [];
    for (let i = 0; i < recipe?.sections?.length; i++) {
      if (recipe?.sections?.[i]?.macros?.length > 0) {
        loggableSections.push(i);
      }
    }
    setOtherLoggableSections(loggableSections);
  };

  useEffect(() => {
    getOtherLoggableSections();
  }, [recipe]);

  useEffect(() => {
    if (modals?.macrosModal?.sectionIndex !== null) {
      setLocalServingsEaten(recipe?.sections?.[modals?.macrosModal?.sectionIndex]?.servingsEaten || 1);
    }
  }, [modals?.macrosModal?.sectionIndex]);

  return (
    <div className="w-full max-w-full md:max-w-[600px] max-h-[80vh]">
      <div className="flex flex-col sm:flex-row justify-between items-center p-4 gap-2 bg-neutral-100">
        <div className="w-full ">
          <Typography variant="h6" className="font-normal">
            Track your Macros
          </Typography>
        </div>
        {otherLoggableSections.length > 1 && (
          <div className="w-full ">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ShadcnButton
                  variant="outline"
                  className="w-full justify-between font-normal rounded-lg border-secondaryText-200 hover:border-primaryText font-sans text-md"
                >
                  {recipe && recipe?.sections?.[modals?.macrosModal?.sectionIndex]?.title
                    ? recipe.sections[modals?.macrosModal?.sectionIndex].title
                    : "Recipe Section"}
                  <ChevronDown className="w-3 opacity-50 ml-2" />
                </ShadcnButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-[8px] z-[1301]">
                {recipe && recipe.sections ? (
                  otherLoggableSections.map((loggableSectionIndex) => (
                    <DropdownMenuItem
                      key={loggableSectionIndex}
                      onSelect={() => {
                        setMacrosModalSection(loggableSectionIndex);
                      }}
                    >
                      <span
                        className={`${
                          loggableSectionIndex === modals?.macrosModal?.sectionIndex ? "font-medium text-mainGreen" : ""
                        }`}
                      >
                        {recipe?.sections?.[loggableSectionIndex]?.title}
                      </span>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem>No sections</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="mb-4 pt-2">
          <TextField
            label="Servings Eaten"
            value={localServingsEaten}
            onChange={handleServingsChange}
            inputProps={{
              maxLength: 2,
            }}
            fullWidth
            helperText={`Max: ${section?.servings} serving${section?.servings > 1 ? "s" : ""}`}
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full mb-4">
            <TabsTrigger value="total" className="flex-1">
              Total Macros
            </TabsTrigger>
            <TabsTrigger value="single" className="flex-1">
              Single Serving
            </TabsTrigger>
          </TabsList>

          <TabsContent value="single">
            <div>
              <Typography variant="subtitle1" className="font-medium leading-snug">
                1 Serving
              </Typography>
              <div className="pb-4 border-b border-secondaryText-600">
                <Typography variant="body2" className="font-normal leading-snug text-secondaryText-600">
                  ({section?.servingSize})
                </Typography>
              </div>
              <div className="mt-4">
                {section?.macros && section.macros.map((macro) => <MacrosItem key={macro.id} data={macro} />)}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="total">
            <div>
              <Typography variant="subtitle1" className="font-medium leading-snug">
                {`${localServingsEaten && localServingsEaten !== "" ? localServingsEaten : 0} ${
                  localServingsEaten === 1 ? "Serving" : "Servings"
                }`}
              </Typography>

              <div className="pb-4 border-b border-secondaryText">
                <Typography variant="body2" className="font-normal leading-snug text-secondaryText-600">
                  ({section?.servingSize})
                </Typography>
              </div>

              <div className="mt-4">
                {section?.macros &&
                  section.macros.map((macro) => (
                    <MacrosItem key={macro.id} data={macro} servings={localServingsEaten} />
                  ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <div className="mt-4">
          <Button variant="contained" color="primary" fullWidth disableElevation onClick={() => handleSave()}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

MacrosModal.propTypes = {
  macros: PropTypes.array.isRequired,
  servingSize: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  totalServings: PropTypes.number.isRequired,
  handleClose: PropTypes.func.isRequired,
  setServingsEaten: PropTypes.func.isRequired,
  servingsEaten: PropTypes.number.isRequired,
  errors: PropTypes.object,
  walkthrough: PropTypes.object.isRequired,
  section: PropTypes.object.isRequired,
  setMacrosModalSection: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  errors: state.walkthrough.errors,
  walkthrough: state.walkthrough,
});

export default connect(mapStateToProps, { setServingsEaten, setMacrosModalSection })(MacrosModal);
